// Sending a subcontractor to Xero as a complete payable contact (Session ARZ, Item 4): one button and its preview, used
// by the Accounting card and by the Payment details panel (which offers "Update Xero contact" once details are verified).
//
// The office sees, before anything is written, what Xero holds NOW beside what the platform would send, field by field,
// with bank details masked on both sides, and what will happen to each: sent, already the same, or kept as Xero has it
// (the platform never blanks a field it has no value for). Nothing is written until the person confirms (a two-step
// button), and a confirm of a preview that Xero or the platform has since moved is refused with the fresh preview.
// No bill and no payment is ever raised (D-SBD-5).
//
// Session AUV (design system Session 8): presentation only. The primary trigger is the shared Button, the two-step confirm takes
// the shared primary Button classes, and its ARMED state fills with the fixed red #c0392b (bg-ele-danger-fill, white ink 5.44:1)
// where it filled with --ele-danger and white ink (3.06:1 on the dark theme: defect D4's shape, found by the contrast ratchet).
import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { buttonClasses, Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import type { XeroContactPreview, XeroPlanAction } from "../../lib/subcontractorPayments";

const btnCls = "rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50";

const ACTION_TEXT: Record<XeroPlanAction, string> = {
  send: "Will be sent",
  same: "Already the same",
  keep: "Left as it is in Xero",
};

export function XeroContactButton({
  subId,
  label,
  primary,
  onDone,
}: {
  subId: string;
  label: string;
  primary?: boolean;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {primary ? (
        <Button onClick={() => setOpen(true)}>{label}</Button>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className={btnCls}>
          {label}
        </button>
      )}
      {open && <XeroContactModal subId={subId} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

function XeroContactModal({ subId, onClose, onDone }: { subId: string; onClose: () => void; onDone?: () => void }) {
  const [preview, setPreview] = useState<XeroContactPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.subcontractors
      .xeroContactPreview(subId)
      .then((p) => live && setPreview(p))
      .catch((e) => live && setError(friendlyApiError(e, "Could not read the contact from Xero. Please try again in a moment.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [subId]);

  async function confirm() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.subcontractors.pushXeroContact(subId, preview.previewToken);
      setDone(
        res.nothingToSend
          ? "Xero already matches. The subcontractor is linked to their Xero contact."
          : res.message ?? (res.created ? "The subcontractor was added to Xero." : "The Xero contact was updated."),
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.preview) {
        // The plan moved (or cannot be written): show the one the server just read, and say why.
        setPreview(e.body.preview as XeroContactPreview);
        setNotice(typeof e.body.message === "string" ? e.body.message : "Xero or the platform changed since this preview. Look at it again, then confirm.");
      } else setError(friendlyApiError(e, "Could not send the contact to Xero."));
    } finally {
      setBusy(false);
    }
  }

  const title = preview?.mode === "create" ? "Add to Xero" : "Update Xero contact";
  // The page is refreshed when the person closes the dialog, not the moment the write lands, so the result stays on
  // screen until they have read it (a refresh can unmount the button this dialog belongs to).
  const close = () => {
    if (busy) return;
    if (done) onDone?.();
    onClose();
  };

  return (
    <ModalShell ariaLabel={title} onClose={close} width="max-w-2xl" margin="my-8" panelClassName="p-6">
      <h2 className="mb-2 font-heading text-2xl tracking-wide text-ele-strong">{title}</h2>
      {loading ? (
        <p className="text-sm text-ele-body">Reading Xero...</p>
      ) : done ? (
        <p className="rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">{done}</p>
      ) : preview ? (
        <>
          <p className="mb-3 text-sm text-ele-body">
            {preview.mode === "create"
              ? "No Xero contact exists for this subcontractor yet. This creates one with the details below. No bill or payment is created."
              : "This is what Xero holds now, and what would change. Nothing is written until you confirm. No bill or payment is created."}
          </p>
          <table className="ele-list-table mb-3 w-full text-left text-sm">
            <thead>
              <tr className="text-ele-body">
                <th className="py-1 pr-2 font-menu text-xs uppercase tracking-wide">Field</th>
                <th className="py-1 pr-2 font-menu text-xs uppercase tracking-wide">In Xero now</th>
                <th className="py-1 pr-2 font-menu text-xs uppercase tracking-wide">From the platform</th>
                <th className="py-1 font-menu text-xs uppercase tracking-wide">What happens</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.key} className="border-t border-ele-purple-accent align-top">
                  <td className="py-1 pr-2 text-ele-body">{r.label}</td>
                  <td className="py-1 pr-2 text-ele-strong">{r.xero ?? (preview.mode === "create" ? "" : "Nothing")}</td>
                  <td className="py-1 pr-2 text-ele-strong">{r.platform ?? "Nothing on file"}</td>
                  <td className={`py-1 ${r.action === "send" ? "text-ele-green" : "text-ele-body"}`}>{ACTION_TEXT[r.action]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.notes.length > 0 && (
            <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-ele-body">
              {preview.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
          {preview.blocked && (
            <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {preview.blocked}
            </p>
          )}
          {notice && <p className="mb-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">{notice}</p>}
        </>
      ) : null}
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-2 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={close} disabled={busy} className={`${btnCls} ${MODAL_ACTION_BTN}`}>
          {done ? "Close" : "Cancel"}
        </button>
        {preview && !done && !preview.blocked && (
          <ConfirmButton
            label={preview.nothingToSend ? (preview.linked ? "Nothing to send" : "Link to this Xero contact") : preview.mode === "create" ? "Add to Xero" : "Send changes to Xero"}
            confirmLabel="Confirm: write this to Xero?"
            busy={busy}
            busyLabel="Sending..."
            disabled={preview.nothingToSend && preview.linked}
            onConfirm={confirm}
            className={buttonClasses("primary", "md", MODAL_ACTION_BTN)}
            armedClassName="!bg-ele-danger-fill !text-white"
          />
        )}
      </div>
    </ModalShell>
  );
}
